import * as React from 'react';
import { QuestionCard } from './QuestionCard';
import { usePersonalityTest } from '../../hooks/usePersonalityTest';
import type { EchoDraftResponse } from '@/types/echo_creation';
import clsx from 'clsx';

interface PersonalitySectionProps {
  draftId: string;
  draft?: EchoDraftResponse | null;
  onComplete?: () => void;
  onSaveForLater: () => void;
}

export const PersonalitySection: React.FC<PersonalitySectionProps> = ({
  draftId,
  draft,
  onComplete,
  onSaveForLater,
}) => {
  const {
    currentIdx,
    currentQuestion,
    totalQuestions,
    answers,
    handleAnswer,
    handleNext,
    handlePrevious,
    saveProgress,
    isSubmitting,
  } = usePersonalityTest(draftId, onComplete, draft);

  return (
    <div
      className={clsx(
        'w-full',
        // Layout: Root stays full width to keep text left-aligned
        isSubmitting && 'pointer-events-none opacity-70'
      )}
    >
      {/* Header Area: Left Aligned */}
      <div className='mb-8'>
        <h2
          className={clsx(
            'mb-2 font-proxima font-extrabold uppercase text-dark-100',
            // Mobile: 24px
            'text-[24px]',
            // Desktop: 40px (personality-title token)
            'md:text-personality-title'
          )}
        >
          Personality Test
        </h2>
        <p
          className={clsx(
            'font-sans font-medium leading-27 text-gray-530',
            // Enforce 16px explicitly
            'text-[16px]'
          )}
        >
          Help us understand your personality
        </p>
      </div>

      {/* Content Area: Centered Card */}
      <div className='w-full wide:mx-auto wide:max-w-[1000px]'>
        {currentQuestion ? (
          <QuestionCard
            question={currentQuestion}
            currentIndex={currentIdx}
            totalQuestions={totalQuestions}
            answer={answers.get(currentQuestion.id)}
            onAnswer={handleAnswer}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onSaveForLater={async () => {
              await saveProgress();
              onSaveForLater();
            }}
            isSubmitting={isSubmitting}
          />
        ) : (
          <div className='w-full rounded-12 bg-gray-100 p-6 text-center'>
            <p className='font-sans text-16 text-gray-600'>No personality questions available.</p>
          </div>
        )}
      </div>
    </div>
  );
};
